import React, { memo } from 'react';
import { UserOutlined, CheckCircleFilled } from '@ant-design/icons';
import type { CandidatePerson } from './types';

/** 候选池状态文案切换时的轻量淡入动画。 */
const statusFadeKeyframes = `
@keyframes candidatePoolStatusFade {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

interface CandidatePoolProps {
  /** 候选池人员（第二步流结束后确定） */
  candidates: CandidatePerson[];
  /** 是否全部已完成任务分配（第三步末尾用）。若 true，则显示"完成任务分配"。 */
  allAssigned: boolean;
  /** 候选池为空但未完成时展示的状态文案 */
  emptyText: string;
  /** 是否处于"新加入"动画态：刚进入候选池时卡片带入场动画 */
  enteringIds?: Set<string>;
  /** 是否处于"匹配离开"动画态：第三步匹配成功后卡片逐个淡出 */
  leavingIds?: Set<string>;
}

/**
 * 候选人队伍面板（DOM 实现）。
 *
 * 参考设计图 1.png / 7.png 底部的"候选人队伍"灰色边框区域：
 *   - 初始空 →  第二步根据流入的 approved 人员逐个出现
 *   - 第三步匹配完成后，清空候选卡片并显示"完成任务分配"
 */
const CandidatePool: React.FC<CandidatePoolProps> = ({
  candidates,
  allAssigned,
  emptyText,
  enteringIds,
  leavingIds,
}) => {
  const statusText = allAssigned ? '完成任务分配' : emptyText;

  return (
    <div className="shrink-0 border-t border-slate-200 bg-slate-50/50 px-5 py-3">
      <style>{statusFadeKeyframes}</style>
      {/* 标题 */}
      <div className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
        候选人队伍
      </div>

      {/* 候选区 —— 虚线边框 */}
      <div className="min-h-[96px] rounded-md border border-dashed border-slate-300 bg-white p-3">
        {allAssigned || candidates.length === 0 ? (
          <div className="flex h-[72px] items-center justify-center">
            <div
              key={statusText}
              className={[
                'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-500',
                allAssigned
                  ? 'bg-sky-100 text-sky-700 shadow-sm'
                  : 'bg-slate-100 text-slate-500',
              ].join(' ')}
              style={{ animation: 'candidatePoolStatusFade 500ms ease' }}
            >
              {allAssigned && (
                <CheckCircleFilled style={{ color: '#10b981', fontSize: 18 }} />
              )}
              {statusText}
            </div>
          </div>
        ) : (
          <div className="flex min-h-[72px] flex-wrap items-center gap-3">
            {candidates.map(person => {
              const theme = person.themeColor || '#64748b';
              const entering = enteringIds?.has(person.id) ?? false;
              const leaving = leavingIds?.has(person.id) ?? false;
              const displayName = person.name.split('\n')[0] || person.name;
              return (
                <div
                  key={person.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2 transition-all duration-500"
                  style={{
                    borderColor: theme,
                    backgroundColor: hexWithAlpha(theme, 0.1),
                    opacity: entering || leaving ? 0.2 : 1,
                    transform: entering
                      ? 'translateY(12px) scale(0.9)'
                      : leaving
                        ? 'translateY(-10px) scale(0.92)'
                        : 'none',
                    minWidth: 160,
                  }}
                >
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: hexWithAlpha(theme, 0.2) }}
                  >
                    <UserOutlined style={{ color: theme, fontSize: 14 }} />
                  </div>
                  <div className="flex flex-col leading-tight">
                    <span className="text-sm font-bold text-slate-800">
                      {displayName}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {person.roles[0] || '未分配角色'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(CandidatePool);

/** 将十六进制颜色加上 alpha 通道，返回 rgba 字符串。简单工具 */
function hexWithAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
